import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { paths } from '../../app/routes'
import type { PreparationRecord } from '../../db/types'
import { startSession } from '../../offline/session'
import { useOnline } from '../../shared/hooks'

/**
 * The Start tap: `POST /sessions`, then into the runner.
 *
 * §5.1: creating the session is online and required. Being honest about that
 * is better than starting a session the server does not know about, so offline
 * the error says the items are ready and only the connection is missing.
 */
export function useStartSession(descriptorId: string): {
  start(preparation: PreparationRecord): Promise<void>
  starting: boolean
  error: string | null
} {
  const navigate = useNavigate()
  const online = useOnline()
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = useCallback(
    async (preparation: PreparationRecord) => {
      setStarting(true)
      setError(null)
      try {
        const session = await startSession({
          descriptorId,
          preparationId: preparation.preparationId,
          itemIds: preparation.itemIds,
        })
        navigate(paths.session(session.id))
      } catch (cause) {
        setError(
          online
            ? `Sezení se nepodařilo založit: ${cause instanceof Error ? cause.message : String(cause)}`
            : 'Pro spuštění je potřeba připojení. Úlohy už jsou stažené — jakmile se připojíte, můžete začít.',
        )
      } finally {
        setStarting(false)
      }
    },
    [descriptorId, navigate, online],
  )

  return { start, starting, error }
}
